import { useEffect, useRef, useState } from 'react';
import { Check, CircleDot, X } from 'lucide-react';
import {
  type RequisitionStatus,
  type RequisitionTransitionDto,
} from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { GeoLink } from '../../../shared/ui/GeoLink.js';
import {
  REQUISITION_MAIN_PATH,
  isStoppedStatus,
  mainPathIndex,
} from '../model/requisition-status.js';
import {
  requisitionPendingText,
  requisitionStatusText,
} from '../../../shared/i18n/labels.js';

/**
 * Bosqichning holati.
 *
 * `current` — HALI BO'LMAGAN, ayni damda kutilayotgan qadam. Ilgari u
 * oxirgi BAJARILGAN o'tishga qo'yilardi va natijada «Korxona tasdiqladi»
 * yozuvi ostida «kutilmoqda» belgisi turardi — ikkovi bir-biriga zid
 * gapirardi. Bajarilgan ish bajarilgan bo'lib qoladi; kutilayotgani esa
 * KEYINGI qadam.
 *
 * `rejected` alohida tur: u shunchaki "tugagan" emas — oqim SHU YERDA
 * to'xtagan va sabab o'qilishi kerak.
 */
type StepState = 'done' | 'current' | 'rejected' | 'upcoming';

export interface StatusTimelineProps {
  transitions: readonly RequisitionTransitionDto[];
  currentStatus: RequisitionStatus;
  /** Kelajak bosqichlarni ko'rsatish — kartochkada ha, ro'yxatda yo'q. */
  showUpcoming?: boolean;
  className?: string;
  /**
   * Qadam QAYERDA bajarilgani — holat bo'yicha koordinata.
   *
   * Tarix yozuvining o'zida (`requisition_transition`) koordinata YO'Q va
   * bo'lishi ham shart emas: u dalolatnomaga tegishli, o'tishga emas —
   * o'n ikkita o'tishning o'ntasi umuman biror joyda "sodir bo'lmaydi",
   * ular ekran ortidagi qaror. Shuning uchun koordinata tashqaridan,
   * dalolatnomalardan keladi.
   */
  locations?: Partial<Record<RequisitionStatus, { lat: number; lng: number }>>;
}

/** Yangi bosqich necha vaqt "yangi" bo'lib turadi. */
const ARRIVAL_MS = 1200;

/**
 * YANGI BOSQICH kelganini bildiradi.
 *
 * Holatni BOSHQA odam o'zgartiradi va ekran uni so'rov bilan o'zi olib
 * keladi (`LIVE_REFETCH_MS`). O'zgarish jimgina kelsa, sahifaga qarab
 * turgan odam uni payqamaydi — u eski suratni ko'rayotgandek qolaveradi.
 *
 * BIRINCHI chizishda hech narsa yonmaydi: sahifa endi ochilgan bo'lsa,
 * butun tarix "hozir sodir bo'ldi" bo'lib ko'rinardi. Belgi faqat
 * ro'yxat ochiq turganda O'ZGARGANDA yonadi.
 */
const useArrivedStep = (lastKey: string | null): string | null => {
  const seen = useRef<string | null>(null);
  const [arrived, setArrived] = useState<string | null>(null);

  useEffect(() => {
    if (lastKey === null) return;
    const first = seen.current === null;
    const changed = seen.current !== lastKey;
    seen.current = lastKey;
    if (first || !changed) return;

    setArrived(lastKey);
    const timer = setTimeout(() => setArrived(null), ARRIVAL_MS);
    return () => clearTimeout(timer);
  }, [lastKey]);

  return arrived;
};

interface TimelineStep {
  key: string;
  status: RequisitionStatus;
  state: StepState;
  /** `null` — bosqich hali bo'lmagan (kelajak). */
  transition: RequisitionTransitionDto | null;
  /** Talabnomaning tug'ilishi — o'tish emas, boshlanish. */
  isBirth: boolean;
}

/** Sana va vaqt: "03.08.2026 11:00" — qisqa va bir xil kenglikda. */
const pad = (value: number): string => String(value).padStart(2, '0');

const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const STATE_LABELS: Record<StepState, string> = {
  done: 'requisition.timelineDone',
  current: 'requisition.timelineCurrent',
  rejected: 'requisition.timelineRejected',
  upcoming: 'requisition.timelineUpcoming',
};

/**
 * Bosqichlar ro'yxati: bo'lib o'tgani tarixdan, qolgani asosiy yo'ldan.
 *
 * Kelajak bosqichlar `REQUISITION_MAIN_PATH` dan olinadi va bu O'TISH QOIDASI
 * emas — "yo'l qanday ko'rinadi" degan savolga javob. Kim qaysi tugmani bosa
 * olishini faqat server aytadi (`availableTransitions`).
 */
export const buildTimeline = (
  transitions: readonly RequisitionTransitionDto[],
  currentStatus: RequisitionStatus,
  showUpcoming: boolean,
): TimelineStep[] => {
  if (transitions.length === 0) {
    // Tarixsiz talabnoma — faqat tug'ilish qatori. Yo'lning qolgani hali yozilmagan.
    return [
      { key: 'birth', status: currentStatus, state: 'done', transition: null, isBirth: true },
    ];
  }

  /* BO'LIB O'TGANI — bajarilgan. To'xtatgan o'tish (rad etish, bekor
     qilish) alohida ajraladi: uning sababi o'qilishi kerak. */
  const steps: TimelineStep[] = transitions.map((transition) => ({
    key: transition.id,
    status: transition.toStatus,
    state: isStoppedStatus(transition.toStatus) ? 'rejected' : 'done',
    transition,
    isBirth: transition.fromStatus === null,
  }));

  if (!showUpcoming || isStoppedStatus(currentStatus)) return steps;

  const from = mainPathIndex(currentStatus);
  if (from < 0) return steps;

  /* KEYINGI qadam — ayni damda kutilayotgani. Undan keyingilari esa hali
     uzoqda va kulrang bo'lib turadi. */
  return [
    ...steps,
    ...REQUISITION_MAIN_PATH.slice(from + 1).map((status, index) => ({
      key: `upcoming:${status}`,
      status,
      state: (index === 0 ? 'current' : 'upcoming') as StepState,
      transition: null,
      isBirth: false,
    })),
  ];
};

const dotClass: Record<StepState, string> = {
  /* BAJARILGAN qadam yashil — pastdagi izoh aytganidek, yashil shu ilovada
     "bo'ldi" degani. Ilgari bu yerda urg'u rangi turardi va u ishlardi,
     chunki urg'uning o'zi yashil edi; SQB palitrasida urg'u ko'k bo'lgach,
     bog'lanish yashil tokenga ko'chirildi — aks holda bajarilgan qadam ko'k
     bo'lib, sariq/yashil/qizil uchligi buzilardi. */
  done: 'border-success bg-success text-success-contrast',
  /* KUTILAYOTGAN qadam — SARIQ, chunki ilovada sariq bitta narsani
     bildiradi: kutilmoqda («Navbatda» belgisi ham shu rangda). Yashil
     esa bajarilganning rangi va uni kutish uchun ishlatish ikki holatni
     bir tusga qo'shib yuborardi.
     To'ldirilmaydi: to'ldirilgan belgi "bo'ldi" degani. Bo'sh kelajak
     qadamlardan uni urg'uli chegara va sekin nafas ajratib turadi. */
  current: 'border-warn bg-warn-soft text-warn [--pulse-color:var(--color-warn)]',
  rejected: 'border-danger bg-danger text-danger-contrast',
  upcoming: 'border-border bg-surface text-text-faint',
};

const titleClass: Record<StepState, string> = {
  done: 'text-text',
  current: 'text-warn font-semibold',
  rejected: 'text-danger font-semibold',
  upcoming: 'text-text-faint',
};

/**
 * Talabnomaning YO'LI — kartochkaning yuragi.
 *
 * Tik ro'yxat: har bosqichda kim, qaysi lavozimda, qachon va nima yozgani.
 * Ekran o'quvchi uchun bu grafik emas, haqiqiy `<ol>` — chunki bu yerdagi
 * ma'lumot bezak emas, hujjatning tarixi.
 *
 * Uch qatlam ko'z bilan ajraladi: bajarilganlar to'ldirilgan yashil belgi
 * bilan, joriysi halqa bilan urg'ulanadi, kelajakdagilari esa bo'sh va kulrang.
 * Rad etilgan bosqich qizil va sababi doim yonida — u yashiringan bo'lsa,
 * xodim nima qilish kerakligini bilmaydi.
 */
export const StatusTimeline = ({
  transitions,
  currentStatus,
  showUpcoming = true,
  className,
  locations,
}: StatusTimelineProps) => {
  const steps = buildTimeline(transitions, currentStatus, showUpcoming);
  const arrived = useArrivedStep(transitions.at(-1)?.id ?? null);

  return (
    <ol aria-label={t('requisition.timelineTitle')} className={cn('flex flex-col', className)}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        /* Shu bosqich hozirgina keldimi — bir martalik harakat uchun. */
        const justArrived = step.key === arrived;
        /* Joy faqat BAJARILGAN qadamda ko'rsatiladi: kelajakdagi qadam
           hali hech qayerda sodir bo'lmagan. */
        const location = step.transition === null ? undefined : locations?.[step.status];

        return (
          <li
            key={step.key}
            data-status={step.status}
            data-state={step.state}
            {...(justArrived ? { 'data-arrived': 'true' } : {})}
            className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3"
          >
            {/* Chap ustun: belgi va uni keyingisiga ulaydigan chiziq. */}
            <span className="flex flex-col items-center">
              <span
                aria-hidden
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
                  'border transition-[background-color,border-color]',
                  'duration-[var(--motion-base)] ease-[var(--ease-standard)]',
                  dotClass[step.state],
                  justArrived ? 'motion-step-pop' : undefined,
                  /* Yangi kelgan bosqichning O'Z harakati bor — ikkovi
                     birga ishlasa halqa ikki marta chiqib, sakragandek
                     ko'rinardi. */
                  step.state === 'current' && !justArrived ? 'motion-pulse-soft' : undefined,
                )}
              >
                {step.state === 'rejected' ? (
                  <X size={13} strokeWidth={2.5} />
                ) : step.state === 'upcoming' ? (
                  <CircleDot size={11} />
                ) : step.state === 'current' ? (
                  /* Kutilayotgan qadamda BELGI yo'q: belgi "bajarildi"
                     degani va u hali bajarilmagan. */
                  <span aria-hidden className="size-1.5 rounded-[var(--radius-pill)] bg-current" />
                ) : (
                  <Check size={13} strokeWidth={2.5} />
                )}
              </span>
              {isLast ? null : (
                <span
                  aria-hidden
                  className={cn(
                    'w-px flex-1',
                    /* Kutilayotgan qadamdan boshlab chiziq UZUQ: yo'l
                       davom etadi, lekin bu qismi hali bosilmagan. */
                    step.state === 'upcoming' || step.state === 'current'
                      ? 'bg-[repeating-linear-gradient(180deg,var(--color-border-strong)_0_4px,transparent_4px_8px)]'
                      : 'bg-accent/35',
                  )}
                />
              )}
            </span>

            <div
              className={cn(
                'min-w-0 pb-5',
                isLast ? 'pb-0' : undefined,
                justArrived ? 'motion-step-row' : undefined,
              )}
            >
              <p className={cn('flex flex-wrap items-baseline gap-x-2 text-sm', titleClass[step.state])}>
                {step.isBirth
                  ? t('requisition.timelineCreated')
                  : step.state === 'current'
                    ? requisitionPendingText(step.status)
                    : requisitionStatusText(step.status)}
                {/* Holat SO'Z bilan ham beriladi: rang ko'rmaydigan odam ham
                    bosqich tugagan-tugamaganini bilishi kerak. Joriy bosqich
                    esa KO'RINADIGAN yorliq oladi — "hozir qayerda?" degan
                    savolga chizmaning o'zi javob berishi kerak. */}
                <span className="sr-only">{t(STATE_LABELS[step.state])}</span>
                {step.transition === null ? null : (
                  <span className="tabular text-xs font-normal text-text-faint">
                    {formatMoment(step.transition.createdAt)}
                  </span>
                )}
              </p>

              {/*
                BAJARUVCHI — ismi va lavozimi bilan.
                Ular yozuv BILAN BIRGA keladi (`actorName`, `actorPosition`):
                ekran ularni boshqa manbadan qidirmaydi va "Xodim #12" ga
                tushib qolmaydi. Nazorat aynan shu ustundan boshlanadi —
                "kim qildi" degan savolga javobsiz tarixning ma'nosi yo'q.
              */}
              {step.transition === null ? null : (
                <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-text-muted">
                  <span className="font-medium text-text-muted">
                    {step.transition.actorName ??
                      (step.transition.actorUserId === null
                        ? t('requisition.timelineSystem')
                        : t('requisition.timelineActor', { id: step.transition.actorUserId }))}
                  </span>
                  {step.transition.actorPosition === null ? null : (
                    <span className="text-text-faint">{step.transition.actorPosition}</span>
                  )}
                </p>
              )}

              {/*
                QAYERDA bajarilgani — faqat joyi bor qadamlarda.
                Mahsulot qo'ldan qo'lga o'tadigan ikki qadam ("berildi",
                "qabul qilindi") uchun "qachon" yetarli emas: mahsulot
                bo'linmaga yetib bormagan bo'lsa, tasdiq QAYERDAN
                qo'yilgani yagona ob'ektiv iz bo'lib qoladi.
              */}
              {location === undefined ? null : (
                <p className="mt-1 text-xs">
                  <GeoLink gps={location} className="text-xs text-text-muted" />
                </p>
              )}

              {step.transition?.reason === null || step.transition?.reason === undefined ? null : (
                <p
                  className={cn(
                    'mt-1.5 rounded-[var(--radius-sm)] border px-2.5 py-1.5 text-xs',
                    step.state === 'rejected'
                      ? 'border-danger/25 bg-danger-soft text-danger'
                      : 'border-border bg-surface-2 text-text-muted',
                  )}
                >
                  {step.transition.reason}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
